import { Link } from "react-router-dom";
import { MapPin, Sparkles, ArrowRight } from "lucide-react";
import { CITIES } from "@/data/cities";
import { STYLES } from "@/data/massageStyles";

// Annuaire SEO cliquable — un clic vers chaque page ville ou technique.
// Réutilisé sur la Home (avant contact) et sur la page /menu détaillée.
export default function SEODirectory({ compact = false }) {
    const groups = CITIES.reduce((acc, c) => {
        (acc[c.region] = acc[c.region] || []).push(c);
        return acc;
    }, {});

    return (
        <section
            id="annuaire"
            data-testid="seo-directory"
            className={`${compact ? "py-14" : "py-20 md:py-28"} bg-[#FAF7F2] scroll-mt-24`}
        >
            <div className="max-w-6xl mx-auto px-6 md:px-12">
                <div className="max-w-2xl mb-10">
                    <div className="text-xs tracking-[0.3em] uppercase text-[#E07A5F] mb-4">
                        Annuaire des soins
                    </div>
                    <h2 className="font-display text-4xl md:text-5xl font-light text-[#2C2A29] tracking-tight leading-tight">
                        Trouvez votre massage en{" "}
                        <span className="italic text-[#E07A5F]">un clic</span>
                    </h2>
                    <p className="mt-4 text-[#6E6862] leading-relaxed text-base md:text-lg">
                        Toutes mes pages dédiées par ville et par technique — cliquez
                        directement sur la vôtre pour découvrir les détails et réserver.
                    </p>
                </div>

                {/* Techniques */}
                <div className="mb-10">
                    <div className="flex items-center gap-2 mb-4">
                        <Sparkles size={16} className="text-[#3D8C95]" />
                        <h3 className="text-sm uppercase tracking-[0.25em] text-[#3D8C95] font-medium">
                            Par technique
                        </h3>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2">
                        {STYLES.map((s) => (
                            <Link
                                key={s.slug}
                                to={`/${s.slug}`}
                                data-testid={`seodir-style-${s.slug}`}
                                className="group px-3 py-3 rounded-2xl bg-white border border-[#E8E2D9] hover:border-[#E07A5F] hover:bg-white transition-all text-sm text-[#2C2A29] flex items-center justify-between gap-2"
                            >
                                <span className="truncate">{s.shortName}</span>
                                <ArrowRight size={12} className="text-[#E07A5F] opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
                            </Link>
                        ))}
                    </div>
                </div>

                {/* Villes par région */}
                {Object.entries(groups).map(([region, cities]) => (
                    <div key={region} className="mb-8">
                        <div className="flex items-center gap-2 mb-4">
                            <MapPin size={16} className="text-[#E07A5F]" />
                            <h3 className="text-sm uppercase tracking-[0.25em] text-[#E07A5F] font-medium">
                                {region}
                            </h3>
                            {cities.some((c) => c.mode === "future") && (
                                <span className="text-[10px] uppercase tracking-widest text-[#B7A46B]">
                                    · ouverture 2027
                                </span>
                            )}
                        </div>
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2">
                            {cities.map((c) => (
                                <Link
                                    key={c.slug}
                                    to={`/${c.slug}`}
                                    data-testid={`seodir-city-${c.slug}`}
                                    className="group px-3 py-3 rounded-2xl bg-white border border-[#E8E2D9] hover:border-[#3D8C95] transition-all text-sm text-[#2C2A29] flex items-center justify-between gap-2"
                                >
                                    <span className="truncate">
                                        Massage {c.city}
                                        {c.mode === "future" && (
                                            <span className="ml-1 text-[10px] text-[#B7A46B]">(2027)</span>
                                        )}
                                    </span>
                                    <ArrowRight size={12} className="text-[#3D8C95] opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
                                </Link>
                            ))}
                        </div>
                    </div>
                ))}

                {/* Pages secondaires */}
                <div>
                    <div className="flex items-center gap-2 mb-4">
                        <h3 className="text-sm uppercase tracking-[0.25em] text-[#6E6862] font-medium">
                            Services & extras
                        </h3>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2">
                        {[
                            ["/menu", "Menu détaillé"],
                            ["/confort-et-outils", "Confort & outils"],
                            ["/massage-entreprise", "Entreprise"],
                            ["/massage-domicile", "À domicile"],
                            ["/evenements", "Événements"],
                            ["/cartes-cadeaux", "Cartes-cadeaux"],
                            ["/blainville", "Blainville"],
                            ["/rive-nord", "Rive-Nord"],
                            ["/biographie", "Biographie"],
                            ["/blog", "Blog"],
                            ["/exercices", "Exercices & Sons"],
                        ].map(([to, label]) => (
                            <Link
                                key={to}
                                to={to}
                                data-testid={`seodir-page-${to.replace(/\//g, "")}`}
                                className="group px-3 py-3 rounded-2xl bg-white border border-[#E8E2D9] hover:border-[#2C2A29] transition-all text-sm text-[#2C2A29] flex items-center justify-between gap-2"
                            >
                                <span className="truncate">{label}</span>
                                <ArrowRight size={12} className="text-[#2C2A29] opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
                            </Link>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
}
